"use client";

import Link from "@/components/locale-link";
import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import {
  HINT,
  NOTICE,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { CHI_SQUARE_CALCULATOR_STRINGS } from "@/components/tools/chi-square-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type {
  GoodnessOfFitResult,
  IndependenceResult,
} from "@/lib/tool-kit/stats-tests/chi-square";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";

/** Results for the chi-square calculator: independence and goodness of fit. */

type Strings = typeof CHI_SQUARE_CALCULATOR_STRINGS;

/** Spell out Σ term by term only while the sum stays readable. */
const MAX_TERMS = 12;

function sumLine(terms: number[], total: number, fmt: NumberFormatter): string {
  const n = (v: number) => formatStat(v, fmt);
  if (terms.length > MAX_TERMS) {
    return n(total);
  }
  return `${terms.map(n).join(" + ")} = ${n(total)}`;
}

function LowExpectedWarning({
  count,
  min,
  is2x2,
  strings,
  locale,
  fmt,
}: {
  count: number;
  min: number;
  is2x2: boolean;
  strings: Strings;
  locale: string;
  fmt: NumberFormatter;
}) {
  if (count === 0) {
    return null;
  }
  return (
    <div className={`${NOTICE} space-y-1`} role="note">
      <p>
        {tPlural(strings.lowExpected, locale, count, { count: fmt(count) })}{" "}
        {t(strings.lowExpectedDetail, { min: formatStat(min, fmt, 2) })}
      </p>
      {is2x2 ? (
        <p>
          {strings.fisherPointer}{" "}
          <Link
            className="text-primary underline-offset-4 hover:underline"
            href="/tools/fisher-exact-test-calculator"
          >
            {strings.fisherLink}
          </Link>
        </p>
      ) : (
        <p>{strings.combinePointer}</p>
      )}
    </div>
  );
}

function decisionText(
  p: number,
  alpha: number,
  reject: string,
  keep: string,
  fmt: NumberFormatter
): string {
  return t(p < alpha ? reject : keep, {
    alpha: fmt(alpha),
    p: formatP(p, fmt),
  });
}

export function IndependenceResults({
  r,
  rowLabels,
  colLabels,
  yates,
  alpha,
  strings,
  locale,
  fmt,
}: {
  r: IndependenceResult;
  rowLabels: readonly string[];
  colLabels: readonly string[];
  yates: boolean;
  alpha: number;
  strings: Strings;
  locale: string;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const corrected = yates && r.yates !== null;
  const chi = corrected && r.yates ? r.yates.chiSquare : r.chiSquare;
  const p = corrected && r.yates ? r.yates.p : r.p;
  const is2x2 = r.phi !== null;
  const rows = r.observed.length;
  const cols = r.colTotals.length;
  const head = ["", ...colLabels];
  const table = (values: number[][], digits: number) =>
    values.map((row, i) => ({
      key: `${i}-${rowLabels[i]}`,
      cells: [rowLabels[i], ...row.map((v) => formatStat(v, fmt, digits))],
    }));
  const apa = richTemplate(
    is2x2 ? strings.apaTemplate2x2 : strings.apaTemplate,
    {
      df: fmt(r.df),
      n: fmt(r.n),
      chi: fixed(chi, 2, fmt),
      p: apaP(p, fmt),
      v: fixed(r.cramersV, 2, fmt, false),
    }
  );
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={corrected ? strings.chiSquareYates : strings.chiSquare}
          value={n(chi)}
        />
        <StatCard label={strings.df} value={fmt(r.df)} />
        <StatCard emphasis label={strings.pValue} value={formatP(p, fmt)} />
        <StatCard label={strings.n} value={fmt(r.n)} />
        <StatCard
          label={is2x2 ? strings.phi : strings.cramersV}
          value={n(r.cramersV)}
        />
        {corrected ? (
          <StatCard label={strings.chiSquare} value={n(r.chiSquare)} />
        ) : null}
      </StatGrid>
      <Decision reject={p < alpha}>
        {decisionText(
          p,
          alpha,
          strings.decisionReject,
          strings.decisionKeep,
          fmt
        )}
      </Decision>
      <LowExpectedWarning
        count={r.cellsBelowFive}
        fmt={fmt}
        is2x2={is2x2}
        locale={locale}
        min={r.minExpected}
        strings={strings}
      />
      <h3 className={WIDGET_H3}>{strings.expectedHeading}</h3>
      <p className={HINT}>{strings.expectedHint}</p>
      <ResultTable head={head} rows={table(r.expected, 2)} />
      <h3 className={WIDGET_H3}>{strings.residualsHeading}</h3>
      <p className={HINT}>{strings.residualsHint}</p>
      <ResultTable head={head} rows={table(r.adjustedResiduals, 2)} />
      <Working heading={strings.howCalculated}>
        <p>{`E₁₁ = R₁ × C₁ / N = ${fmt(r.rowTotals[0])} × ${fmt(r.colTotals[0])} / ${fmt(r.n)} = ${n(r.expected[0][0])}`}</p>
        <p>{`χ² = Σ (O − E)² / E = ${sumLine(r.contributions.flat(), r.chiSquare, fmt)}`}</p>
        {r.yates ? (
          <p>{`χ²(Yates) = Σ (|O − E| − 0.5)² / E = ${n(r.yates.chiSquare)}`}</p>
        ) : null}
        <p>{`df = (r − 1)(c − 1) = (${fmt(rows)} − 1)(${fmt(cols)} − 1) = ${fmt(r.df)}`}</p>
        <p>{`p = P(χ²(${fmt(r.df)}) ≥ ${n(chi)}) = ${formatP(p, fmt)}`}</p>
        <p>{`V = √(χ² / (N × (min(r, c) − 1))) = √(${n(r.chiSquare)} / (${fmt(r.n)} × ${fmt(Math.min(rows, cols) - 1)})) = ${n(r.cramersV)}`}</p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={corrected ? strings.apaNoteYates : undefined}
        parts={apa}
      />
    </section>
  );
}

export function FitResults({
  r,
  names,
  customExpected,
  alpha,
  strings,
  locale,
  fmt,
}: {
  r: GoodnessOfFitResult;
  names: readonly string[];
  /** Whether the reader typed expected values (else equal shares were assumed). */
  customExpected: boolean;
  alpha: number;
  strings: Strings;
  locale: string;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const name = (i: number) =>
    names[i] ?? t(strings.categoryDefault, { n: i + 1 });
  const apa = richTemplate(strings.apaTemplateFit, {
    df: fmt(r.df),
    n: fmt(r.n),
    chi: fixed(r.chiSquare, 2, fmt),
    p: apaP(r.p, fmt),
    v: fixed(r.cohensW, 2, fmt),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.chiSquare} value={n(r.chiSquare)} />
        <StatCard label={strings.df} value={fmt(r.df)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard label={strings.n} value={fmt(r.n)} />
        <StatCard label={strings.cohensW} value={n(r.cohensW)} />
      </StatGrid>
      <Decision reject={r.p < alpha}>
        {decisionText(
          r.p,
          alpha,
          strings.decisionRejectFit,
          strings.decisionKeepFit,
          fmt
        )}
      </Decision>
      <LowExpectedWarning
        count={r.cellsBelowFive}
        fmt={fmt}
        is2x2={false}
        locale={locale}
        min={r.minExpected}
        strings={strings}
      />
      <ResultTable
        head={[
          strings.categoryColumn,
          strings.observedColumn,
          strings.proportionColumn,
          strings.expectedColumn,
          strings.contributionColumn,
        ]}
        rows={r.observed.map((o, i) => ({
          key: `${i}-${name(i)}`,
          cells: [
            name(i),
            fmt(o),
            n(r.proportions[i]),
            n(r.expected[i]),
            n(r.contributions[i]),
          ],
        }))}
      />
      {customExpected ? (
        <p className={HINT}>{t(strings.rescaledNote, { n: fmt(r.n) })}</p>
      ) : null}
      <Working heading={strings.howCalculated}>
        <p>{`E = N × p = ${fmt(r.n)} × ${n(r.proportions[0])} = ${n(r.expected[0])} …`}</p>
        <p>{`χ² = Σ (O − E)² / E = ${sumLine(r.contributions, r.chiSquare, fmt)}`}</p>
        <p>{`df = k − 1 = ${fmt(r.observed.length)} − 1 = ${fmt(r.df)}`}</p>
        <p>{`p = P(χ²(${fmt(r.df)}) ≥ ${n(r.chiSquare)}) = ${formatP(r.p, fmt)}`}</p>
        <p>{`w = √(χ² / N) = √(${n(r.chiSquare)} / ${fmt(r.n)}) = ${n(r.cohensW)}`}</p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        parts={apa}
      />
    </section>
  );
}
